import { useI18n } from "@/i18n/I18nProvider";
import { Reveal } from "@/hooks/useReveal";
import { SEO } from "@/components/SEO";
import { Link } from "react-router-dom";
import { PresenceDotMap } from "@/components/PresenceDotMap";
import {
  ArrowUpRight,
  FileText,
  Users,
  CalendarRange,
  Receipt,
  Kanban,
  Workflow,
  Sun,
  FolderOpen,
  KeyRound,
  MessagesSquare,
  UserCog,
  LifeBuoy,
  type LucideIcon,
} from "lucide-react";

const offices = ["Guatemala", "United States", "Spain", "Belarus", "Poland", "Portugal", "Dubai"];

const PILLARS: { key: string; Icon: LucideIcon }[] = [
  { key: "quote", Icon: FileText },
  { key: "resources", Icon: Users },
  { key: "planning", Icon: CalendarRange },
  { key: "billing", Icon: Receipt },
  { key: "pm", Icon: Kanban },
  { key: "methodology", Icon: Workflow },
  { key: "daily", Icon: Sun },
  { key: "docs", Icon: FolderOpen },
  { key: "creds", Icon: KeyRound },
  { key: "coord", Icon: MessagesSquare },
  { key: "specialists", Icon: UserCog },
  { key: "support", Icon: LifeBuoy },
];

export default function About() {
  const { t } = useI18n();
  const pillars = t.about.osPillars as Record<string, { t: string; d: string }>;
  return (
    <>
      <SEO title={t.nav.about} description={t.about.lead} />

      <section className="mx-auto max-w-7xl px-6 lg:px-10 pt-24 lg:pt-32 pb-24">
        <Reveal>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-6">
            — {t.about.eyebrow}
          </p>
        </Reveal>
        <Reveal delay={120}>
          <h1 className="font-display text-5xl lg:text-8xl font-semibold tracking-tighter leading-[0.95] max-w-5xl text-balance">
            {t.about.title}
          </h1>
        </Reveal>
        <Reveal delay={240}>
          <p className="mt-10 max-w-3xl text-xl lg:text-2xl text-muted-foreground leading-relaxed text-pretty">
            {t.about.lead}
          </p>
        </Reveal>
      </section>

      <section className="border-t hairline">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-2 gap-16">
          <Reveal>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-4">— 01</p>
            <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tighter mb-6">
              {t.about.philosophyTitle}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">{t.about.philosophyBody}</p>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-4">— 02</p>
            <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tighter mb-6">
              {t.about.visionTitle}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">{t.about.visionBody}</p>
          </Reveal>
        </div>
      </section>

      <section className="border-t hairline bg-foreground/[0.015]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
          <Reveal>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-6">— 03</p>
            <h2 className="font-display text-4xl lg:text-7xl font-semibold tracking-tighter max-w-4xl text-balance">
              {t.about.modernTitle}
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-10 max-w-3xl text-lg lg:text-xl text-muted-foreground leading-relaxed">
              {t.about.modernBody}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t hairline">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
          <Reveal>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-6">
              — 04 · {t.about.osEyebrow}
            </p>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-display text-4xl lg:text-7xl font-semibold tracking-tighter max-w-5xl text-balance">
              {t.about.osTitle}
            </h2>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-6 max-w-3xl text-base lg:text-lg text-muted-foreground leading-relaxed text-pretty">
              {t.about.osTitleSub}
            </p>
          </Reveal>
          <Reveal delay={220}>
            <p className="mt-10 max-w-3xl text-lg lg:text-xl text-muted-foreground leading-relaxed text-pretty">
              {t.about.osLead}
            </p>
          </Reveal>

          <div className="mt-20 max-w-4xl">
            <Reveal>
              <h3 className="font-display text-3xl lg:text-5xl font-semibold tracking-tighter mb-6">
                {t.about.osPillarsTitle}
              </h3>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {t.about.osPillarsBody}
              </p>
            </Reveal>
          </div>

          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 border hairline rounded-2xl overflow-hidden">
            {PILLARS.map(({ key, Icon }, i) => {
              const p = pillars[key];
              const col = i % 3;
              const row = Math.floor(i / 3);
              return (
                <Reveal key={key} delay={i * 40}>
                  <div
                    className={[
                      "p-6 h-full min-h-[160px] flex flex-col gap-3 hover:bg-foreground/[0.03] transition-colors",
                      "border-b hairline",
                      col !== 2 ? "lg:border-r hairline" : "",
                      row === 3 ? "lg:border-b-0" : "",
                    ].join(" ")}
                  >
                    <div className="flex items-center justify-between">
                      <Icon className="h-5 w-5 text-foreground/80" strokeWidth={1.5} />
                      <span className="text-[10px] font-mono text-muted-foreground/60">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <span className="font-display text-lg font-semibold tracking-tight">
                      {p.t}
                    </span>
                    <span className="text-sm text-muted-foreground leading-relaxed">
                      {p.d}
                    </span>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <div className="mt-24 max-w-4xl">
            <Reveal>
              <h3 className="font-display text-3xl lg:text-5xl font-semibold tracking-tighter mb-6">
                {t.about.osClientTitle}
              </h3>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {t.about.osClientBody}
              </p>
            </Reveal>
          </div>

          <div className="mt-24 max-w-4xl">
            <Reveal>
              <div className="rounded-2xl border hairline bg-card/40 p-6 md:p-10 space-y-6">
                <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  {t.about.ceoKicker}
                </p>
                <p className="text-lg md:text-2xl font-light italic text-foreground/85 leading-relaxed text-pretty">
                  “{t.about.ceoQuote}”
                </p>
                <div className="flex items-center gap-5 pt-4">
                  <img
                    src="/team-avatars/tp-100-jorge.png"
                    alt={t.about.ceoName}
                    loading="lazy"
                    className="h-24 w-24 rounded-full object-cover border hairline"
                  />
                  <div className="leading-snug">
                    <div className="text-base font-medium text-foreground">{t.about.ceoName}</div>
                    <div className="text-sm text-muted-foreground">{t.about.ceoRole}</div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="mt-24 max-w-4xl">
            <Reveal>
              <h3 className="font-display text-3xl lg:text-5xl font-semibold tracking-tighter mb-6 text-balance">
                {t.about.osUniqueTitle}
              </h3>
              <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed text-pretty">
                {t.about.osUniqueBody}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="relative border-t hairline bg-neutral-950 text-white overflow-hidden">
        <div
          className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 w-full opacity-50"
          aria-hidden
        >
          <PresenceDotMap />
        </div>
        <div
          className="pointer-events-none absolute inset-0"
          aria-hidden
          style={{
            background:
              "linear-gradient(to bottom, rgb(10 10 10) 0%, rgb(10 10 10 / 0.55) 18%, rgb(10 10 10 / 0) 38%, rgb(10 10 10 / 0) 62%, rgb(10 10 10 / 0.55) 82%, rgb(10 10 10) 100%)",
          }}
        />

        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
          <div className="mb-12 max-w-4xl">
            <Reveal>
              <p className="text-xs uppercase tracking-wider text-white/60 mb-4">— 05</p>
              <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tighter text-balance">
                {t.about.presenceTitle}
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <p className="mt-6 text-base lg:text-lg text-white/60 max-w-2xl text-pretty">
                {t.about.presenceBody}
              </p>
            </Reveal>
          </div>
          <div className="relative grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 border border-white/10 rounded-2xl overflow-hidden bg-neutral-950/85 backdrop-blur-sm">
            {offices.map((c, i) => (
              <Reveal key={c} delay={i * 60}>
                <div className="border-b lg:border-b-0 lg:border-r border-white/10 last:border-r-0 p-6 h-full flex flex-col justify-between min-h-[140px] hover:bg-white/[0.04] transition-colors">
                  <span className="text-[10px] font-mono text-white/40">0{i + 1}</span>
                  <span className="font-display text-lg font-semibold tracking-tight">
                    {(t.countries as Record<string, string>)[c]}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="relative mt-16 flex justify-center">
            <Link to="/contact" className="inline-flex h-12 items-center gap-2 rounded-full bg-white px-6 text-sm font-medium text-neutral-950 hover:opacity-90 transition">
              {t.cta.start}
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}